import React, { useState, useEffect } from "react";
import {
  Dialog,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Close, LockOutlined, CheckCircleOutline } from "@mui/icons-material";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import Box from "themeComponents/Box";
import Button from "themeComponents/Button";
import { paymentApi } from "configs/api";

const stripePublishableKey =
  process.env.REACT_APP_ENV === "dev"
    ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_DEV || ""
    : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

const stripePromise = loadStripe(
  stripePublishableKey
);

// ─── Inner form (needs to be inside <Elements>) ───────────────────────────────

interface PaymentFormProps {
  amount: number;
  onSuccess: (paymentIntentId: string) => Promise<void>;
  onCancel: () => void;
}

function PaymentForm({ amount, onSuccess, onCancel }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setError(null);
    setSubmitting(true);

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // No redirect — we handle success inline
        return_url: window.location.href,
      },
      redirect: "if_required",
    });

    if (stripeError) {
      setError(stripeError.message ?? "Payment failed. Please try again.");
      setSubmitting(false);
    } else if (paymentIntent?.status === "succeeded") {
      try {
        await onSuccess(paymentIntent.id);
      } catch (confirmError: any) {
        setError(
          confirmError?.response?.data?.message ||
            confirmError?.message ||
            "Payment was processed but confirmation failed. Please contact support."
        );
        setSubmitting(false);
      }
    } else {
      setError("Payment is not completed yet. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <PaymentElement
        options={{
          layout: "tabs",
        }}
      />

      {/* Amount summary */}
      <Box
        sx={{
          mt: 2.5,
          px: 2,
          py: 1.5,
          borderRadius: "10px",
          background: "linear-gradient(135deg, #f5f7ff 0%, #eef2fb 100%)",
          border: "1px solid rgba(25,118,210,0.15)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          Total charged
        </Typography>
        <Typography variant="h6" fontWeight={800} color="#1a237e">
          ${amount.toFixed(2)}
        </Typography>
      </Box>

      <Box display="flex" gap={1.5} justifyContent="flex-end" mt={2.5}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={onCancel}
          disabled={submitting}
          sx={{ borderRadius: "10px", px: 2.5 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="gradient"
          color="info"
          size="small"
          disabled={!stripe || !elements || submitting}
          sx={{ borderRadius: "10px", px: 2.5, minWidth: 140 }}
        >
          {submitting ? (
            <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
          ) : (
            <LockOutlined sx={{ fontSize: 16, mr: 1 }} />
          )}
          {submitting ? "Processing…" : `Pay $${amount.toFixed(2)}`}
        </Button>
      </Box>
    </form>
  );
}

// ─── Success state ─────────────────────────────────────────────────────────────

interface SuccessViewProps {
  courseTitle: string;
  quota: number;
  amount: number;
  onClose: () => void;
}

function SuccessView({ courseTitle, quota, amount, onClose }: SuccessViewProps) {
  return (
    <Box textAlign="center" py={2}>
      <CheckCircleOutline sx={{ fontSize: 64, color: "#2e7d32", mb: 2 }} />
      <Typography variant="h6" fontWeight={700} mb={0.5}>
        Payment Successful!
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={0.5}>
        {courseTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {quota === -1 ? "Unlimited seats" : `${quota} ${quota === 1 ? "seat" : "seats"}`} · ${amount.toFixed(2)} charged
      </Typography>
      <Button
        variant="gradient"
        color="success"
        size="small"
        onClick={onClose}
        sx={{ borderRadius: "10px", px: 3 }}
      >
        Done
      </Button>
    </Box>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

export interface StripePaymentDialogProps {
  open: boolean;
  onClose: () => void;
  courseID: string;
  courseTitle: string;
  quota: number;
  price: number;
  onPaymentComplete?: () => void;
}

function StripePaymentDialog({
  open,
  onClose,
  courseID,
  courseTitle,
  quota,
  price,
  onPaymentComplete,
}: StripePaymentDialogProps): React.JSX.Element {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [serverAmount, setServerAmount] = useState<number>(price);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  // Create PaymentIntent when dialog opens
  useEffect(() => {
    if (!open || !courseID) return;
    setClientSecret(null);
    setIntentError(null);
    setPaid(false);
    setServerAmount(price);
    setLoadingIntent(true);

    paymentApi
      .post("/api/payments/course/purchase", { courseID, quota })
      .then((res) => {
        const secret = res.data?.data?.paymentIntent?.client_secret;
        const amountInCents = res.data?.data?.paymentIntent?.amount;
        if (!secret) throw new Error("No client secret returned from server.");
        setClientSecret(secret);
        if (typeof amountInCents === "number" && Number.isFinite(amountInCents)) {
          setServerAmount(amountInCents / 100);
        }
      })
      .catch((err) => {
        setIntentError(
          err?.response?.data?.message || err?.message || "Failed to initialize payment."
        );
      })
      .finally(() => setLoadingIntent(false));
  }, [open, courseID, quota, price]);

  const handleSuccess = async (paymentIntentId: string) => {
    setConfirmingPayment(true);
    setIntentError(null);
    try {
      await paymentApi.post("/api/payments/confirm", {
        paymentIntentId,
      });

      setPaid(true);
      onPaymentComplete?.();
    } catch (error: any) {
      setIntentError(
        error?.response?.data?.message ||
          error?.message ||
          "Payment confirmation failed. Please contact support."
      );
    } finally {
      setConfirmingPayment(false);
    }
  };

  const handleClose = () => {
    // Prevent closing during intent load; allow close on error or after success
    if (loadingIntent) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #1976d2 100%)",
          px: 3,
          pt: 3,
          pb: 2.5,
          position: "relative",
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={loadingIntent}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            bgcolor: "rgba(255,255,255,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.5,
          }}
        >
          <LockOutlined sx={{ color: "#fff", fontSize: 22 }} />
        </Box>

        <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>
          {paid ? "Payment Complete" : "Secure Payment"}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "rgba(255,255,255,0.75)",
            mt: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {courseTitle}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, pt: 3, pb: 3 }}>
        {paid ? (
          <SuccessView
            courseTitle={courseTitle}
            quota={quota}
            amount={serverAmount}
            onClose={onClose}
          />
        ) : loadingIntent || confirmingPayment ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              {loadingIntent ? "Initializing secure payment…" : "Finalizing payment confirmation…"}
            </Typography>
          </Box>
        ) : intentError ? (
          <Box>
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {intentError}
            </Alert>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={onClose}
                sx={{ borderRadius: "10px" }}
              >
                Close
              </Button>
            </Box>
          </Box>
        ) : clientSecret ? (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: "stripe",
                variables: {
                  colorPrimary: "#1976d2",
                  borderRadius: "10px",
                  fontFamily: '"Inter", sans-serif',
                },
              },
            }}
          >
            <PaymentForm
              amount={serverAmount}
              onSuccess={handleSuccess}
              onCancel={onClose}
            />
          </Elements>
        ) : null}
      </Box>
    </Dialog>
  );
}

export default StripePaymentDialog;
